import { Injectable } from '@angular/core';
import { TriggerbeeApi } from './../core/triggerbee/triggerbee-requester.service';
import { Logger, LoggerFactory } from './../core/logger.service';
import { HttpRequestDecorator } from 'app/core/triggerbee/http.request.decorator';
import { map, Observable } from 'rxjs';
import {
  AiChatUsage,
  AiChatUsageSummary,
  CalculateSubscriptionPriceRequest,
  ConvertToShopifyResponse,
  TierLimitations,
  Limitations,
  ManangeSubscriptionResponse,
  PublicApiCallsUsage,
  ShopifyAppChargeResponse,
  StorageLimitations,
  SubscriptionDetails,
  SubscriptionFrequency,
  UpdateSubscriptionPriceRequest,
} from './subscription.model';
import { Language } from 'app/core/language.service';
import { DateService } from 'app/shared/services/date-service';
import { CustomerModel } from 'app/account-settings/models/customer.model';
import { Tier } from 'app/shared/account/tier.model';
import { Currency } from '../shared/account/Currency';

@Injectable()
export class SubscriptionService {
  private readonly logger: Logger;
  private static readonly unlimitedLimitValue = 2147483647; // int.MaxValue in C#, same as we put in DB when they bought unlimited

  constructor(
    loggerFactory: LoggerFactory,
    private readonly http: HttpRequestDecorator,
    private readonly dateService: DateService,
  ) {
    this.logger = loggerFactory.get('SubscriptionService');
  }

  public getLimitations(customerId: string): Observable<Limitations> {
    const url = `${this.http.getBaseUrl(TriggerbeeApi.Gateway)}/subscriptions/limitations/${customerId}`;
    return this.http.get(url);
  }

  public getPriceCalculation(request: CalculateSubscriptionPriceRequest): Observable<number> {
    const url = `${this.http.getBaseUrl(TriggerbeeApi.Gateway)}/subscriptions/calculate-price`;
    return this.http.post(url, request);
  }

  public getTierLimitations(tier?: Tier): Observable<TierLimitations> {
    const customerHasTier = !!tier;
    tier = tier ? tier : Tier.Pro; // Backward compatibility for SelfService-customers without "eventsPerMonth"
    const url = `${this.http.getBaseUrl(TriggerbeeApi.Gateway)}/subscriptions/limitations/tiers/${tier}`;
    return this.http.get(url).pipe(
      map((tierLimitations: TierLimitations) => {
        if (!customerHasTier) {
          // Backward compatibility for customers without tier
          tierLimitations.defaultSurveyResponses = 50; // Value all customers had before Tiers was implemented
        }

        // Add default values
        tierLimitations.events.push(undefined); // Last item (without a value) we use to show "Please contact sales@triggerbee.com for larger packages."
        tierLimitations.surveyResponses.unshift(0); // First item we use for responses included in current tier
        return tierLimitations;
      }),
    );
  }

  public savePriceCalculation(customerId: string, request: UpdateSubscriptionPriceRequest): Observable<number> {
    const url = `${this.http.getBaseUrl(TriggerbeeApi.Gateway)}/subscriptions/${customerId}/price`;
    return this.http.put(url, request);
  }

  public updateTier(customerId: string, tier: string): Observable<number> {
    const url = `${this.http.getBaseUrl(TriggerbeeApi.Gateway)}/subscriptions/${customerId}/tier`;
    return this.http.put(url, `"${tier}"`);
  }

  public createCheckoutSession(
    customer: CustomerModel,
    returnUrl: string,
    language: Language,
    billingFrequency: SubscriptionFrequency,
    currency: Currency,
    limitations: Limitations,
    tier: Tier,
    testPrice?: number,
  ): Observable<ManangeSubscriptionResponse> {
    const url = `${this.http.getBaseUrl(TriggerbeeApi.Gateway)}/subscriptions/${customer.id}/checkout-session`;
    return this.http.post(url, {
      returnUrl: returnUrl,
      billingFrequency: billingFrequency,
      currency: currency,
      language: Language[language],
      eventsPerMonth: limitations.eventsPerMonth,
      accounts: limitations.accounts,
      surveyResponsesPerMonth: limitations.surveyResponsesPerMonth,
      testPrice: testPrice,
      tier: tier,
    });
  }

  public createPortalSession(
    customerId: string,
    returnUrl: string,
    language: Language,
  ): Observable<ManangeSubscriptionResponse> {
    const url = `${this.http.getBaseUrl(TriggerbeeApi.Gateway)}/subscriptions/${customerId}/portal-session`;
    return this.http.post(url, {
      returnUrl: returnUrl,
      language: Language[language],
    });
  }

  public createShopifyAppCharge(
    customer: CustomerModel,
    returnUrl: string,
    language: Language,
    billingFrequency: SubscriptionFrequency,
    currency: Currency,
    limitations: Limitations,
    tier: Tier,
    testPrice?: number,
  ): Observable<ShopifyAppChargeResponse> {
    const url = `${this.http.getBaseUrl(TriggerbeeApi.Gateway)}/subscriptions/${customer.id}/checkout-session`;
    return this.http
      .post(url, {
        returnUrl: returnUrl,
        billingFrequency: billingFrequency,
        currency: currency,
        language: Language[language],
        eventsPerMonth: limitations.eventsPerMonth,
        accounts: limitations.accounts,
        surveyResponsesPerMonth: limitations.surveyResponsesPerMonth,
        testPrice: testPrice,
        tier: tier,
      })
      .pipe(map((response: ManangeSubscriptionResponse) => ({ confirmationUrl: response.url })));
  }

  public confirmShopifyCharge(customerId: string, chargeId: string): Observable<void> {
    const url = `${this.http.getBaseUrl(TriggerbeeApi.Gateway)}/subscriptions/${customerId}/shopify-confirm`;
    return this.http.post(url, { chargeId: chargeId });
  }

  public cancelShopifySubscription(customerId: string): Observable<void> {
    const url = `${this.http.getBaseUrl(TriggerbeeApi.Gateway)}/subscriptions/${customerId}/shopify-cancel`;
    return this.http.post(url, {});
  }

  public convertToShopify(customerId: string): Observable<ConvertToShopifyResponse> {
    const url = `${this.http.getBaseUrl(TriggerbeeApi.Gateway)}/customers/${customerId}/convert-to-shopify`;
    return this.http.post(url, {});
  }

  public detectShopifyDomain(customerId: string, domain: string): Observable<ConvertToShopifyResponse> {
    const url = `${this.http.getBaseUrl(TriggerbeeApi.Gateway)}/customers/${customerId}/detect-shopify-domain`;
    return this.http.post(url, { domain });
  }

  public getSubscription(customerId: string): Observable<SubscriptionDetails> {
    const url = `${this.http.getBaseUrl(TriggerbeeApi.Gateway)}/subscriptions/${customerId}`;
    return this.http.get(url);
  }

  public getEmailCount(customerId: string, startDate: Date, endDate: Date): Observable<number> {
    const url = `${this.http.getBaseUrl(TriggerbeeApi.Gateway)}/usage-statistics/Emails/${customerId}/count`;
    return this.http.get(url, {
      startDate: this.dateService.format(startDate),
      endDate: this.dateService.format(endDate),
    });
  }

  public getEmailCountAdmin(customerId: string, startDate: Date, endDate: Date): Observable<number> {
    const url = `${this.http.getBaseUrl(TriggerbeeApi.Gateway)}/admin/usage-statistics/Emails/${customerId}/count`;
    return this.http.get(url, {
      startDate: this.dateService.format(startDate),
      endDate: this.dateService.format(endDate),
    });
  }

  public getPublicApiCallsCount(customerId: string, startDate: Date, endDate: Date): Observable<number> {
    const url = `${this.http.getBaseUrl(TriggerbeeApi.Gateway)}/usage-statistics/PublicApiCalls/${customerId}/count`;
    return this.http.get(url, {
      startDate: this.dateService.format(startDate),
      endDate: this.dateService.format(endDate),
    });
  }

  public getPublicApiCallsCountAdmin(customerId: string, startDate: Date, endDate: Date): Observable<number> {
    const url = `${this.http.getBaseUrl(TriggerbeeApi.Gateway)}/admin/usage-statistics/PublicApiCalls/${customerId}/count`;
    return this.http.get(url, {
      startDate: this.dateService.format(startDate),
      endDate: this.dateService.format(endDate),
    });
  }

  public getAiChatUsageAdmin(customerId: string): Observable<AiChatUsage> {
    const url = `${this.http.getBaseUrl(TriggerbeeApi.Gateway)}/admin/usage-statistics/ai-chat/${customerId}/usage`;
    return this.http.get(url);
  }

  public getAiChatUsage(): Observable<AiChatUsageSummary> {
    const url = `${this.http.getBaseUrl(TriggerbeeApi.Gateway)}/usage-statistics/ai-chat/usage`;
    return this.http.get(url);
  }

  public getPublicApiCallsUsage(customerId: string): Observable<PublicApiCallsUsage> {
    const url = `${this.http.getBaseUrl(TriggerbeeApi.Gateway)}/usage-statistics/public-api-calls/${customerId}/usage`;
    return this.http.get(url);
  }

  public getPublicApiCallsUsageAdmin(customerId: string): Observable<PublicApiCallsUsage> {
    const url = `${this.http.getBaseUrl(TriggerbeeApi.Gateway)}/admin/usage-statistics/public-api-calls/${customerId}/usage`;
    return this.http.get(url);
  }

  public createNewAccounts(
    customerId: string,
    storageLimitations: StorageLimitations,
    previousAccounts: number,
  ): Observable<any> {
    const url = `${this.http.getBaseUrl(TriggerbeeApi.Gateway)}/subscriptions/${customerId}/accounts`;
    return this.http.post(url, {
      tier: storageLimitations.tier,
      accounts: storageLimitations.accounts,
      previousAccounts: previousAccounts,
    });
  }

  public setLimitationsToSessionStorage(key: string, currentSiteId: number, limitations: StorageLimitations) {
    const sessionStorageKey = `${key}-${currentSiteId}`;
    if (limitations) {
      sessionStorage.setItem(sessionStorageKey, JSON.stringify(limitations));
    } else {
      sessionStorage.removeItem(sessionStorageKey);
    }
  }

  public getLimitationsFromSessionStorage(key: string, currentSiteId: number): StorageLimitations {
    return <StorageLimitations>JSON.parse(sessionStorage.getItem(`${key}-${currentSiteId}`));
  }

  public isCustomPlan(customerLimitations: Limitations, tierLimitations: TierLimitations) {
    if (!tierLimitations.events.includes(customerLimitations.eventsPerMonth)) {
      return true;
    }
    if (!tierLimitations.surveyResponses.includes(customerLimitations.surveyResponsesPerMonth)) {
      return true;
    }
    return false;
  }

  public getStartDate(resetDayInMonth: number): Date {
    const startDate = this.dateService.nowLocal();
    const resetDayInMonthIsNotPassed = startDate.getDate() < resetDayInMonth;

    // Check if reset-day in month is passed and we need to change the month we use to get stats
    if (resetDayInMonthIsNotPassed) {
      let month: number;
      const currentMonth = startDate.getMonth();

      // Check if current month is January
      if (currentMonth === 0) {
        // Then we should set to December last year
        month = 11; // 11 = December
        startDate.setFullYear(startDate.getFullYear() - 1);
      } else {
        // Otherwise we just decrease current month with 1
        month = currentMonth - 1;
      }

      // Set the new calculated month
      startDate.setMonth(month);
    }

    // Change date from today to the day in month it was reset last time
    startDate.setDate(resetDayInMonth);

    return startDate;
  }

  public getTotalSurveyResponses(surveyResponsesLimit: number, defaultSurveyResponses: number): number {
    let totalSurveyResponses: number = surveyResponsesLimit || 0; // Amount stored on customer limitations (can be undefined);
    totalSurveyResponses += defaultSurveyResponses; // Responses customer always get included
    return totalSurveyResponses;
  }

  public isUnlimited(value: number): boolean {
    return SubscriptionService.unlimitedLimitValue === value;
  }

  public isSurveyResponsesUnlimited(value: number): boolean {
    return this.isUnlimited(value);
  }
}
